import io from 'socket.io-client';
import { toastr } from 'react-redux-toastr';
import consts from '../consts';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.connected = false;
    this.baseURL = consts.OAPI_URL.replace('/oapi', '');
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 15;
    this.isAuthenticated = false; // Indica se já autenticamos nesta sessão
    this.pendingAuthData = null; // Armazena dados do usuário se tryAuthenticate for chamado antes de connect
    this.authAttempted = false; // Para evitar múltiplas tentativas se algo der errado
    this.lastErrorList = null; // Cache da última lista recebida para replays
    
    // Expor o serviço no console para depuração
    if (typeof window !== 'undefined') {
      window.socketDebug = {
        getStatus: this.getStatus.bind(this),
        reconnect: () => {
          this.disconnect();
          this.connect();
          return "Tentando reconectar...";
        },
        countListeners: () => {
          const counts = {};
          this.listeners.forEach((list, event) => {
            counts[event] = list.length;
          });
          return counts;
        }
      };
    }
  }

  // Iniciar conexão
  connect() {
    if (this.socket) {
        if (!this.connected) {
            this.socket.connect(); // Tenta reconectar se já existe mas desconectou
        } else if (this.connected && !this.isAuthenticated && this.pendingAuthData) {
             this.performAuthentication(this.pendingAuthData);
        }
        return;
    }
    
    this.authAttempted = false; // Reseta a tentativa a cada nova conexão
    this.isAuthenticated = false;

    try {
      this.socket = io(this.baseURL, {
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        timeout: 20000,
        transports: ['websocket', 'polling'],
        forceNew: true
      });

      this.socket.on('connect', () => {
        this.connected = true; 
        this.isAuthenticated = false; // Resetar status de autenticação na conexão/reconexão
        this.reconnectAttempts = 0;
        
        // Tenta autenticar imediatamente se os dados estiverem pendentes
        if (this.pendingAuthData) {
            this.performAuthentication(this.pendingAuthData);
        }
      });

      // EVENTO 1: Lista completa de erros ativos - emitido pelo backend no login
      this.socket.on('erros_ativos_lista', (erros) => {
        // Validar se é um array
        if (!Array.isArray(erros)) {
          console.warn('WEBSOCKET: Lista de erros inválida recebida (esperava array). Ignorando.', erros);
          return;
        }

        // Atualiza cache
        this.lastErrorList = erros;

        // Disparar o evento interno para o Navbar processar a lista
        if (this.listeners.has('lista_erros_atualizada')) {
            const callbacks = [...this.listeners.get('lista_erros_atualizada')];
            callbacks.forEach(callback => {
                try {
                    callback(erros, 'lista_completa'); // Passa a lista inteira e o tipo de evento
                } catch(e) {
                    console.error('WEBSOCKET: ERRO ao executar callback para lista_erros_atualizada:', e);
                }
            });
        } else {
            console.warn('WEBSOCKET: Sem listeners registrados para lista_erros_atualizada');
        }
      });

      // EVENTO 1b: Lista de erros atualizada (em ciclos do backend)
      this.socket.on('lista_erros_atualizada', (erros) => {
        if (!Array.isArray(erros)) {
          console.warn('WEBSOCKET: Lista de erros inválida recebida (esperava array). Ignorando.', erros);
          return;
        }

        // Atualiza cache
        this.lastErrorList = erros;

        if (this.listeners.has('lista_erros_atualizada')) {
          const callbacks = [...this.listeners.get('lista_erros_atualizada')];
          callbacks.forEach(callback => {
            try {
              callback(erros, 'lista_atualizada');
            } catch (e) {
              console.error('WEBSOCKET: ERRO ao executar callback para lista_erros_atualizada:', e);
            }
          });
        }
      });

      // EVENTO 2: Novo erro individual - único evento emitido pelo backend para novas notificações
      this.socket.on('novo_erro', (erro) => {
        if (!erro || typeof erro !== 'object') {
          console.warn('WEBSOCKET: Formato de erro inválido recebido. Ignorando.', erro);
          return;
        }

        // Encaminhar para listeners de 'novo_erro'
        if (this.listeners.has('novo_erro') && this.listeners.get('novo_erro').length > 0) {
          const callbacks = [...this.listeners.get('novo_erro')];
          callbacks.forEach(callback => {
            try {
              callback(erro, 'novo_erro'); // Passa o erro único e o tipo de evento
            } catch (e) {
              console.error('WEBSOCKET: ERRO ao executar callback para novo_erro:', e);
            }
          });
        } else {
             console.warn('WEBSOCKET: Sem listeners registrados para novo_erro');
        }
      });

      // Eventos de conexão/reconexão
      this.socket.on('disconnect', (reason) => {
        this.connected = false;
        this.isAuthenticated = false;
        this.authAttempted = false; // Permite nova tentativa na reconexão
      });

      this.socket.on('reconnect', (attemptNumber) => {
        this.connected = true;
        this.reconnectAttempts = 0;
        if (this.pendingAuthData) {
          this.performAuthentication(this.pendingAuthData);
        }
      });
      
      this.socket.on('reconnect_attempt', (attemptNumber) => {
        this.reconnectAttempts = attemptNumber;
        
        if (attemptNumber >= this.maxReconnectAttempts) {
          this.disconnect();
          setTimeout(() => this.connect(), 5000);
        }
      });

      this.socket.on('connect_error', (error) => {
        console.error('Erro de conexão WebSocket:', error);
      });
    } catch (error) {
      console.error('Erro ao inicializar WebSocket:', error);
    }
  }

  // Chamado pela aplicação (authActions.js) após login/token válido
  tryAuthenticate() {
      this.authAttempted = true; // Marca que a aplicação tentou
      
      try {
          const userKey = '_ssat';
          const userString = localStorage.getItem(userKey);

          if (!userString) {
              console.warn(`(tryAuthenticate) Falha: ${userKey} não encontrado no localStorage.`);
              this.pendingAuthData = null; // Garante que não há dados pendentes
              return; 
          }

          let user = null;
          try {
              user = JSON.parse(userString);
          } catch (parseError) {
              console.error(`(tryAuthenticate) Falha ao parsear JSON (${userKey}):`, parseError);
              this.pendingAuthData = null;
              return; 
          }

          if (user && user.email) {
              // Armazena os dados necessários para a autenticação
              this.pendingAuthData = { email: user.email }; 
              
              // AGORA, verifica se já está conectado para autenticar imediatamente
              if (this.connected) {
                  this.performAuthentication(this.pendingAuthData);
              }
          } else {
               console.warn('(tryAuthenticate) Dados do usuário inválidos (sem email?) após parse.');
               this.pendingAuthData = null;
          }
      } catch (error) {
          console.error('(tryAuthenticate) Erro geral:', error);
          this.pendingAuthData = null;
      }
  }

  // Função interna que realmente envia o evento 'authenticate'
  performAuthentication(authData) {
      if (!this.connected) {
          console.warn('(performAuthentication) Tentativa de autenticar, mas socket não está conectado. Abortando.');
          this.authAttempted = false; // Permite nova tentativa
          return;
      }
      if (this.isAuthenticated) {
          return;
      }
      if (!authData || !authData.email) {
           console.error('(performAuthentication) Dados de autenticação inválidos. Abortando.', authData);
           this.authAttempted = false; // Permite nova tentativa
           return;
      }
    
      try {
            const hostname = window.location.hostname;
            const client = hostname.includes('localhost') ? 'localhost' : hostname.split('.')[0];
            
            const emitData = { 
              email: authData.email,
              client: client 
            };
            
            this.socket.emit('authenticate', emitData);
            this.isAuthenticated = true; // Marca como autenticado
            this.pendingAuthData = null; // Limpa os dados pendentes após sucesso
            
      } catch (error) {
          console.error('(performAuthentication) Erro ao emitir authenticate:', error);
          this.isAuthenticated = false; // Falhou, não está autenticado
          this.authAttempted = false; // Permite nova tentativa
      }
  }

  // Adicionar listener para eventos
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    
    this.listeners.get(event).push(callback);

    // Repassar imediatamente a última lista para novos listeners do sino
    if (event === 'lista_erros_atualizada' && Array.isArray(this.lastErrorList)) {
      try {
        callback(this.lastErrorList, 'cache_replay');
      } catch (e) {
        console.error('WEBSOCKET: ERRO ao executar callback (cache_replay):', e);
      }
    }
  }

  // Remover listener
  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Obter status atual da conexão
  getStatus() {
    // Contar listeners para ambos os eventos
    const listenersCount = {};
    this.listeners.forEach((list, event) => {
        listenersCount[event] = list.length;
    });

    return {
      connected: this.connected,
      isAuthenticated: this.isAuthenticated,
      listenersCount: listenersCount,
      reconnectAttempts: this.reconnectAttempts,
      socketId: this.socket ? this.socket.id : null
    };
  }

  // Desconectar
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
    // Limpar estado na desconexão manual
    this.socket = null;
    this.connected = false;
    this.isAuthenticated = false;
    this.pendingAuthData = null;
    this.authAttempted = false;
  }
}

// Singleton instance
const socketService = new SocketService();

// Adicionar ao window para debug
if (typeof window !== 'undefined') {
  window.socketService = socketService;
}

export default socketService;